import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import { blogTranslations } from '../../translations/blogTranslations';
import { useLanguage } from '../../contexts/LanguageContext';
import { BlogPost as BlogPostType } from '../../translations/blogTranslations';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const navigate = useNavigate();

  // Dynamically generate blog content from translations with proper typing
  const blogContent = React.useMemo(() => {
    const posts = blogTranslations[language].blog.posts;
    return Object.entries(posts).reduce<Record<string, BlogPostType>>((acc, [key, post]) => ({
      ...acc,
      [key]: {
        title: post.title,
        date: post.date,
        readingTime: post.readingTime,
        metaDescription: post.metaDescription,
        content: post.content
      }
    }), {});
  }, [language]);

  const post = blogContent[slug as keyof typeof blogContent];

  React.useEffect(() => {
    if (!post) {
      navigate(`/${language}/blog`, { replace: true });
      return;
    }

    // Enhanced meta tags for SEO
    document.title = `${post.title} | PDF to XRechnung Converter`;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', post.metaDescription);
    }
    
    // Add article specific meta tags
    const metaTags = [
      { property: 'og:title', content: post.title },
      { property: 'og:description', content: post.metaDescription },
      { property: 'og:type', content: 'article' },
      { property: 'article:published_time', content: post.date },
      { name: 'twitter:title', content: post.title },
      { name: 'twitter:description', content: post.metaDescription }
    ];

    metaTags.forEach(tag => {
      let element = document.querySelector(`meta[${Object.keys(tag)[0]}="${Object.values(tag)[0]}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(Object.keys(tag)[0], Object.values(tag)[0]);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });
  }, [post, navigate, language]);

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-12 pt-20">
        <div className="mb-8">
          <Link 
            to={`/${language}/blog`}
            className="inline-flex items-center text-[var(--brand-primary)] hover:text-[var(--brand-primary)]/80 transition-colors"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5 mr-2" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path 
                fillRule="evenodd" 
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" 
                clipRule="evenodd" 
              />
            </svg>
            Blog
          </Link>
        </div>
        <article>
          <h1 className="text-4xl font-bold text-[var(--text-primary)] mb-4">{post.title}</h1>
          <time className="text-[var(--text-tertiary)] block mb-8" dateTime={post.date}>
            {post.date}
          </time>
          <div 
            className="prose prose-invert prose-primary max-w-none"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;